const UBUNIFU_LOGO_URL = 'https://ubunifucollege.com/assets/images/logo-main-black.png';
const STUDENT_DASHBOARD_URL = 'https://live.ubunifucollege.com/login';
const ADMISSIONS_EMAIL = 'admissions@ubunifucollege.com';
const ADMISSIONS_PHONE = '+254 727 634 101';

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const escapeAttribute = (value) => escapeHtml(value).replace(/`/g, '&#096;');

const getDateObject = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);

  const dateObj = new Date(value);
  if (Number.isNaN(dateObj.getTime())) return null;
  return dateObj;
};

const formatDate = (value, fallback = 'Not set') => {
  const dateObj = getDateObject(value);
  if (!dateObj) return fallback;

  return dateObj.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

const formatAmount = (value, currency = 'KES') => {
  const rawValue = String(value ?? '').trim();
  const safeCurrency = String(currency || 'KES').toUpperCase() === 'USD' ? 'USD' : 'KES';

  if (typeof value === 'string' && /[a-z]/i.test(rawValue)) {
    return rawValue;
  }

  const numericAmount = Number(value || 0);
  if (!Number.isFinite(numericAmount)) return `${safeCurrency} 0`;

  if (safeCurrency === 'USD') {
    return `USD ${numericAmount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  }

  return `KES ${numericAmount.toLocaleString('en-KE', {
    maximumFractionDigits: 0
  })}`;
};

const buildDetailsBlock = (details = []) => {
  const rows = details
    .filter((detail) => detail?.label)
    .filter((detail) => detail.value !== undefined && detail.value !== null && detail.value !== '')
    .map((detail) => `
      <strong>${escapeHtml(detail.label)}:</strong> ${escapeHtml(detail.value)}<br>
    `)
    .join('');

  if (!rows) return '';

  return `
    <div style="background:#f8fafc;border:1px solid #e5e7eb;border-radius:14px;padding:18px 20px;margin-bottom:24px;">
      <p style="margin:0 0 12px;color:#475467;font-size:12px;font-weight:700;letter-spacing:1.3px;text-transform:uppercase;">Details</p>
      <p style="margin:0;color:#101828;font-size:15px;line-height:1.8;">
        ${rows}
      </p>
    </div>
  `;
};

const buildCta = ({ href, label, variant = 'primary' } = {}) => {
  if (!href || !label) return '';

  const style = variant === 'secondary'
    ? 'display:inline-block;background:#ffffff;color:#111827;text-decoration:none;padding:12px 18px;border-radius:999px;font-size:14px;font-weight:700;border:1px solid #d1d5db;margin-left:8px;margin-top:8px;'
    : 'display:inline-block;background:#111827;color:#ffffff;text-decoration:none;padding:12px 18px;border-radius:999px;font-size:14px;font-weight:700;margin-top:8px;';

  return `<a href="${escapeAttribute(href)}" target="_blank" style="${style}">${escapeHtml(label)}</a>`;
};

export const buildMinimalEmail = ({
  eyebrow,
  title,
  intro,
  details = [],
  bodyHtml = '',
  primaryCta,
  secondaryCta,
  footer = true
}) => {
  const ctas = [buildCta(primaryCta), buildCta(secondaryCta)].filter(Boolean).join('');

  return `
    <!doctype html>
    <html>
      <body style="margin:0;padding:0;background:#f4f6fb;font-family:Arial,Helvetica,sans-serif;color:#101828;">
        <div style="max-width:640px;margin:0 auto;padding:32px 12px;">
          <div style="background:#ffffff;border:1px solid #e6eaf2;border-radius:18px;overflow:hidden;">
            <div style="background:#111827;padding:30px;">
              <img src="${UBUNIFU_LOGO_URL}" style="height:38px;display:block;margin-bottom:28px;background:#ffffff;border-radius:999px;padding:8px;" alt="Ubunifu College">
              <p style="margin:0 0 12px;color:#a7f3d0;font-size:12px;font-weight:700;letter-spacing:1.6px;text-transform:uppercase;">${escapeHtml(eyebrow || 'Ubunifu College')}</p>
              <h1 style="margin:0;color:#ffffff;font-size:28px;line-height:1.2;">${escapeHtml(title || 'Update from Ubunifu College')}</h1>
              ${intro ? `<p style="margin:14px 0 0;color:#d1d5db;font-size:15px;line-height:1.7;">${escapeHtml(intro)}</p>` : ''}
            </div>

            <div style="padding:30px;">
              ${buildDetailsBlock(details)}
              ${bodyHtml}
              ${ctas ? `<div style="margin-top:28px;">${ctas}</div>` : ''}
            </div>

            ${footer ? `
              <div style="padding:22px 30px;background:#f8fafc;border-top:1px solid #e5e7eb;">
                <p style="margin:0;color:#667085;font-size:13px;line-height:1.6;">Ubunifu College<br>
                ${ADMISSIONS_EMAIL} | ${ADMISSIONS_PHONE}</p>
              </div>
            ` : ''}
          </div>
        </div>
      </body>
    </html>
  `;
};

export const buildAdmissionFeeReceiptEmail = ({
  studentName,
  course,
  amount,
  currency = 'KES',
  paymentReference,
  prospectAdmissionNumber,
  usdEquivalent,
  sourceCurrency = currency,
  usdToKesRate,
}) => {
  const safeChargeCurrency = String(sourceCurrency || currency || 'KES').toUpperCase() === 'USD' ? 'USD' : 'KES';
  const safeUsdEquivalent = Number.isFinite(Number(usdEquivalent))
    ? Number(usdEquivalent)
    : Number(amount || 0) / Number(usdToKesRate || 1);
  const safeRate = Number.isFinite(Number(usdToKesRate)) && Number(usdToKesRate) > 0
    ? `1 USD = ${formatAmount(usdToKesRate, 'KES')}`
    : 'Not set';

  return buildMinimalEmail({
    eyebrow: 'Admission fee received',
    title: `Hi ${studentName || 'there'}, your admission fee is confirmed.`,
    intro: `We have received your admission fee for ${course || 'your selected program'}.`,
    details: [
      { label: 'Amount', value: formatAmount(amount, currency) },
      { label: 'Charged in', value: safeChargeCurrency },
      { label: 'USD equivalent', value: formatAmount(safeUsdEquivalent, 'USD') },
      { label: 'Rate', value: safeRate },
      { label: 'Reference', value: paymentReference },
      { label: 'Application ref', value: prospectAdmissionNumber || 'Will be assigned shortly' }
    ],
    bodyHtml: `
      <p style="margin:0;color:#667085;font-size:14px;line-height:1.7;">Our admissions team will now guide you to schedule your interview and confirm the next step in your enrollment.</p>
    `,
    primaryCta: { href: STUDENT_DASHBOARD_URL, label: 'Open student account' }
  });
};

export const buildReceiptEmail = ({
  studentName,
  amount,
  currency = 'KES',
  paymentDate,
  paymentMethod,
  receiptNumber,
  usdEquivalent,
  sourceCurrency = currency,
  usdToKesRate,
}) => {
  const receiptUrl = `https://live.ubunifucollege.com/view-receipt?${encodeURIComponent(receiptNumber)}`;
  const safeChargeCurrency = String(sourceCurrency || currency || 'KES').toUpperCase() === 'USD' ? 'USD' : 'KES';
  const safeUsdEquivalent = Number.isFinite(Number(usdEquivalent))
    ? Number(usdEquivalent)
    : Number(amount || 0) / Number(usdToKesRate || 1);
  const safeRate = Number.isFinite(Number(usdToKesRate)) && Number(usdToKesRate) > 0
    ? `1 USD = ${formatAmount(usdToKesRate, 'KES')}`
    : 'Not set';

  return buildMinimalEmail({
    eyebrow: 'Payment receipt',
    title: `Thank you, ${studentName || 'there'}. Your payment has been received.`,
    intro: 'We have recorded your payment successfully. Keep this receipt for your records.',
    details: [
      { label: 'Receipt', value: `#${receiptNumber}` },
      { label: 'Amount paid', value: formatAmount(amount, currency) },
      { label: 'Charged in', value: safeChargeCurrency },
      { label: 'USD equivalent', value: formatAmount(safeUsdEquivalent, 'USD') },
      { label: 'Rate', value: safeRate },
      { label: 'Payment date', value: formatDate(paymentDate) },
      { label: 'Payment method', value: paymentMethod || 'Not specified' }
    ],
    bodyHtml: `
      <p style="margin:0;color:#667085;font-size:14px;line-height:1.7;">You can view the receipt online any time using the button below.</p>
    `,
    primaryCta: { href: receiptUrl, label: 'View receipt' },
    secondaryCta: { href: STUDENT_DASHBOARD_URL, label: 'Student login', variant: 'secondary' }
  });
};

export const buildAdmissionConfirmationEmail = ({
  studentName,
  admissionNumber,
  course,
  duration,
  admissionDate,
  classStartDate,
  cohort
}) => {
  const classStartLabel = getDateObject(classStartDate)
    ? formatDate(classStartDate)
    : classStartDate || cohort || 'To be confirmed';

  return buildMinimalEmail({
    eyebrow: 'Admission confirmed',
    title: `Welcome to Ubunifu College, ${studentName || 'there'}.`,
    intro: `You have been admitted to ${course || 'your selected program'}. We are excited to have you join the Ubunifu community.`,
    details: [
      { label: 'Admission number', value: admissionNumber },
      { label: 'Program', value: course || 'Your selected program' },
      { label: 'Duration', value: duration || 'To be confirmed' },
      { label: 'Admission date', value: formatDate(admissionDate) },
      { label: 'Class start date', value: classStartLabel }
    ],
    bodyHtml: `
      <p style="margin:0;color:#667085;font-size:14px;line-height:1.7;">Your next step is to log in to your student account and follow the onboarding instructions. This is where you will see your learning details, payment information, and updates from the admissions team.</p>
    `,
    primaryCta: { href: STUDENT_DASHBOARD_URL, label: 'Open student account' }
  });
};

export const buildSecondaryCourseEmail = ({ studentName, courseName, intakeLabel }) => {
  return buildMinimalEmail({
    eyebrow: 'Course added',
    title: `Hi ${studentName || 'there'}, your secondary course has been added.`,
    intro: 'Please review the course details and complete the invoice step to activate this intake.',
    details: [
      { label: 'Course', value: courseName || 'Course' },
      { label: 'Intake', value: intakeLabel || 'Not set' }
    ],
    bodyHtml: `
      <p style="margin:0;color:#667085;font-size:14px;line-height:1.7;">Go to your invoices page when you are ready to complete payment and finalize admission for this course.</p>
    `,
    primaryCta: { href: STUDENT_DASHBOARD_URL, label: 'Open student account' }
  });
};
